/**
 * AllFilesView - displays all attachments from all tasks across all projects and boards in the organization
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { formatDistanceToNow } from '@/i18n/dateFns';
import {
  File,
  Image,
  FileText,
  Film,
  Music,
  Archive,
  Download,
  Trash2,
  Eye,
  Loader2,
  Grid,
  List,
  Search,
  Paperclip,
} from 'lucide-react';
import { commentsApi, tasksApi, boardsApi, projectsApi, type Attachment, type Task, type Board, type Project } from '@/services/api';
import { toast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import AttachmentViewer from '@/components/board/AttachmentViewer';
import { useAuthStore } from '@/stores/authStore';

interface AllFilesViewProps {
  searchQuery?: string;
}

interface AttachmentWithContext extends Attachment {
  taskId: string;
  taskTitle: string;
  taskKey: string;
  boardId: string;
  boardName: string;
  projectId: string;
  projectName: string;
}

type ViewMode = 'grid' | 'list';
type SortField = 'filename' | 'createdAt' | 'sizeBytes' | 'taskTitle' | 'boardName' | 'projectName';
type SortDirection = 'asc' | 'desc';

// File type icons mapping
const getFileIcon = (contentType: string | undefined) => {
  if (!contentType) return File;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  if (contentType.startsWith('audio/')) return Music;
  if (contentType.includes('pdf') || contentType.includes('document') || contentType.includes('text'))
    return FileText;
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed'))
    return Archive;
  return File;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Get file type category for filtering
const getFileCategory = (contentType: string | undefined): string => {
  if (!contentType) return 'other';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  if (contentType.includes('pdf')) return 'pdf';
  if (contentType.startsWith('text/') || contentType.includes('document')) return 'document';
  if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed'))
    return 'archive';
  return 'other';
};

export function AllFilesView({ searchQuery: externalSearchQuery = '' }: AllFilesViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrgId } = useAuthStore();

  const [attachments, setAttachments] = useState<AttachmentWithContext[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');

  // Viewer state
  const [viewingAttachment, setViewingAttachment] = useState<AttachmentWithContext | null>(null);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState<AttachmentWithContext | null>(null);

  const searchQuery = externalSearchQuery || localSearchQuery;

  // Load all attachments from all tasks across all boards and projects
  const loadAllAttachments = useCallback(async () => {
    if (!currentOrgId) return;

    setIsLoading(true);
    const allAttachments: AttachmentWithContext[] = [];

    try {
      // Fetch all projects
      const projectsResponse = await projectsApi.list(currentOrgId);
      const projects = projectsResponse.items;

      // Build project lookup map
      const projectMap: Record<string, Project> = {};
      for (const project of projects) {
        projectMap[project.id] = project;
      }

      // For each project, fetch all boards and their tasks
      for (const project of projects) {
        const boardsResponse = await boardsApi.list(project.id);
        const boards = boardsResponse.items;

        // Build board lookup map
        const boardMap: Record<string, Board> = {};
        for (const board of boards) {
          boardMap[board.id] = board;
        }

        // Fetch all tasks for each board
        const allTasks: (Task & { boardName: string })[] = [];
        for (const board of boards) {
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const response = await tasksApi.list({ boardId: board.id }, page, 100);
            for (const task of response.items) {
              allTasks.push({ ...task, boardName: board.name });
            }
            hasMore = response.hasMore;
            page++;
          }
        }

        // Fetch attachments for each task in parallel
        await Promise.all(
          allTasks.map(async (task) => {
            try {
              const taskAttachments = await commentsApi.getTaskAttachments(task.id);
              taskAttachments.forEach((attachment) => {
                allAttachments.push({
                  ...attachment,
                  taskId: task.id,
                  taskTitle: task.title,
                  taskKey: task.taskKey,
                  boardId: task.boardId,
                  boardName: task.boardName,
                  projectId: project.id,
                  projectName: project.name,
                });
              });
            } catch (error) {
              console.error(`Failed to load attachments for task ${task.id}:`, error);
            }
          })
        );
      }

      setAttachments(allAttachments);
    } catch (error) {
      console.error('Failed to load files:', error);
      toast.error(t('files.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrgId, t]);

  useEffect(() => {
    void loadAllAttachments();
  }, [loadAllAttachments]);

  // Filter attachments
  const filteredAttachments = useMemo(() => {
    return attachments.filter((attachment) => {
      // Type filter
      if (filterType && getFileCategory(attachment.contentType) !== filterType) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          attachment.filename.toLowerCase().includes(query) ||
          attachment.taskTitle.toLowerCase().includes(query) ||
          attachment.taskKey.toLowerCase().includes(query) ||
          attachment.boardName.toLowerCase().includes(query) ||
          attachment.projectName.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [attachments, filterType, searchQuery]);

  // Sort attachments
  const sortedAttachments = useMemo(() => {
    return [...filteredAttachments].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'filename':
          comparison = a.filename.localeCompare(b.filename);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'sizeBytes':
          comparison = a.sizeBytes - b.sizeBytes;
          break;
        case 'taskTitle':
          comparison = a.taskTitle.localeCompare(b.taskTitle);
          break;
        case 'boardName':
          comparison = a.boardName.localeCompare(b.boardName);
          break;
        case 'projectName':
          comparison = a.projectName.localeCompare(b.projectName);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredAttachments, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleView = (attachment: AttachmentWithContext) => {
    setViewingAttachment(attachment);
  };

  const handleDownload = async (attachment: AttachmentWithContext) => {
    try {
      const { url } = await commentsApi.getAttachment(attachment.id);
      if (url) {
        const { accessToken } = useAuthStore.getState();
        const response = await fetch(url, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = attachment.filename;
        a.click();

        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDelete = (attachment: AttachmentWithContext) => {
    setDeletingAttachment(attachment);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAttachment) return;

    try {
      await commentsApi.deleteAttachment(deletingAttachment.id);
      setAttachments((prev) => prev.filter((a) => a.id !== deletingAttachment.id));
      toast.success(t('files.deleteSuccess'));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error(t('files.deleteError'));
    } finally {
      setShowDeleteConfirm(false);
      setDeletingAttachment(null);
    }
  };

  const handleTaskClick = (attachment: AttachmentWithContext) => {
    navigate(`/boards/${attachment.boardId}?task=${attachment.taskId}`);
  };

  const getAttachmentUrl = useCallback(async (id: string): Promise<string> => {
    const { url } = await commentsApi.getAttachment(id);
    return url || '';
  }, []);

  // Get unique file types for filter dropdown
  const fileTypes = useMemo(() => {
    const types = new Set(attachments.map((a) => getFileCategory(a.contentType)));
    return Array.from(types).sort();
  }, [attachments]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (!currentOrgId) {
    return (
      <div className="h-full flex items-center justify-center text-neutral-500 dark:text-neutral-400">
        {t('files.pleaseSelectOrg')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      {/* Toolbar */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-4 flex-wrap">
        {/* Search */}
        {!externalSearchQuery && (
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder={t('files.searchPlaceholder')}
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
        >
          <option value="">{t('files.allTypes')}</option>
          {fileTypes.map((type) => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={`${sortField}-${sortDirection}`}
          onChange={(e) => {
            const [field, direction] = e.target.value.split('-') as [SortField, SortDirection];
            setSortField(field);
            setSortDirection(direction);
          }}
          className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
        >
          <option value="createdAt-desc">{t('files.sortByDate')}</option>
          <option value="createdAt-asc">{t('files.sortByDateAsc')}</option>
          <option value="filename-asc">{t('files.sortByName')}</option>
          <option value="filename-desc">{t('files.sortByNameDesc')}</option>
          <option value="sizeBytes-desc">{t('files.sortBySize')}</option>
          <option value="sizeBytes-asc">{t('files.sortBySizeAsc')}</option>
          <option value="taskTitle-asc">{t('files.sortByTask')}</option>
          <option value="boardName-asc">{t('files.sortByBoard')}</option>
          <option value="projectName-asc">{t('files.sortByProject')}</option>
        </select>

        {/* View mode toggle */}
        <div className="flex items-center bg-neutral-100 dark:bg-neutral-700 rounded-md p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={clsx(
              'p-1.5 rounded',
              viewMode === 'grid'
                ? 'bg-white dark:bg-neutral-600 text-primary-600 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
            title={t('files.gridView')}
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={clsx(
              'p-1.5 rounded',
              viewMode === 'list'
                ? 'bg-white dark:bg-neutral-600 text-primary-600 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
            title={t('files.listView')}
          >
            <List className="h-4 w-4" />
          </button>
        </div>

        {/* File count */}
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          {t('files.fileCount', { count: sortedAttachments.length })}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {sortedAttachments.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-neutral-500 dark:text-neutral-400">
            <Paperclip className="h-12 w-12 mb-4" />
            <p className="text-lg font-medium">{t('files.noFilesFound')}</p>
            <p className="text-sm">
              {attachments.length === 0
                ? t('files.noFilesEmptyDescription')
                : t('files.noFilesDescription')}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {sortedAttachments.map((attachment) => (
              <FileGridItem
                key={attachment.id}
                attachment={attachment}
                onView={() => handleView(attachment)}
                onDownload={() => void handleDownload(attachment)}
                onDelete={() => handleDelete(attachment)}
                onTaskClick={() => handleTaskClick(attachment)}
              />
            ))}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('filename')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnFile')}
                    <SortIcon field="filename" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('projectName')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnProject')}
                    <SortIcon field="projectName" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('boardName')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnBoard')}
                    <SortIcon field="boardName" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('taskTitle')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnTask')}
                    <SortIcon field="taskTitle" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('sizeBytes')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnSize')}
                    <SortIcon field="sizeBytes" />
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-2">
                    {t('files.columnUploaded')}
                    <SortIcon field="createdAt" />
                  </div>
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  {t('files.columnActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
              {sortedAttachments.map((attachment) => (
                <FileListRow
                  key={attachment.id}
                  attachment={attachment}
                  onView={() => handleView(attachment)}
                  onDownload={() => void handleDownload(attachment)}
                  onDelete={() => handleDelete(attachment)}
                  onTaskClick={() => handleTaskClick(attachment)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Attachment Viewer */}
      <AttachmentViewer
        attachment={viewingAttachment}
        attachments={sortedAttachments}
        isOpen={viewingAttachment !== null}
        onClose={() => setViewingAttachment(null)}
        onNavigate={(a) => setViewingAttachment(a as AttachmentWithContext)}
        getAttachmentUrl={getAttachmentUrl}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingAttachment(null);
        }}
        onConfirm={() => void handleConfirmDelete()}
        title={t('files.confirmDeleteTitle')}
        description={t('files.confirmDeleteDescription', { filename: deletingAttachment?.filename })}
        confirmLabel={t('files.confirmDeleteLabel')}
        variant="danger"
      />
    </div>
  );
}

// Grid item component
interface FileGridItemProps {
  attachment: AttachmentWithContext;
  onView: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onTaskClick: () => void;
}

function FileGridItem({ attachment, onView, onDownload, onDelete, onTaskClick }: FileGridItemProps) {
  const { t } = useTranslation();
  const FileIcon = getFileIcon(attachment.contentType);
  const isImage = attachment.contentType?.startsWith('image/') ?? false;

  return (
    <div
      className={clsx(
        'group relative bg-neutral-50 dark:bg-neutral-700 rounded-lg overflow-hidden',
        'border border-neutral-200 dark:border-neutral-600',
        'hover:border-primary-500 dark:hover:border-primary-500 transition-colors'
      )}
    >
      {/* Preview/Icon area */}
      <div
        className="aspect-square flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 cursor-pointer"
        onClick={onView}
      >
        {isImage && attachment.thumbnailUrl ? (
          <img
            src={attachment.thumbnailUrl}
            alt={attachment.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <FileIcon className="h-12 w-12 text-neutral-400" />
        )}
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate" title={attachment.filename}>
          {attachment.filename}
        </p>
        <p
          className="text-xs text-primary-600 dark:text-primary-400 truncate cursor-pointer hover:underline"
          title={`${attachment.projectName} / ${attachment.boardName} / ${attachment.taskTitle}`}
          onClick={onTaskClick}
        >
          {attachment.taskKey}
        </p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">
          {formatFileSize(attachment.sizeBytes)}
        </p>
      </div>

      {/* Hover actions */}
      <div
        className={clsx(
          'absolute top-2 right-2 flex items-center gap-1',
          'opacity-0 group-hover:opacity-100 transition-opacity'
        )}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          className="p-1.5 rounded bg-white/90 dark:bg-neutral-800/90 text-neutral-600 hover:text-primary-600 shadow-sm"
          title={t('files.viewFile')}
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          className="p-1.5 rounded bg-white/90 dark:bg-neutral-800/90 text-neutral-600 hover:text-neutral-900 shadow-sm"
          title={t('files.downloadFile')}
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1.5 rounded bg-white/90 dark:bg-neutral-800/90 text-neutral-600 hover:text-red-600 shadow-sm"
          title={t('files.deleteFile')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// List row component
interface FileListRowProps {
  attachment: AttachmentWithContext;
  onView: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onTaskClick: () => void;
}

function FileListRow({ attachment, onView, onDownload, onDelete, onTaskClick }: FileListRowProps) {
  const { t } = useTranslation();
  const FileIcon = getFileIcon(attachment.contentType);
  const timeAgo = formatDistanceToNow(new Date(attachment.createdAt), { addSuffix: true });

  return (
    <tr className="hover:bg-neutral-50 dark:hover:bg-neutral-700">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded bg-neutral-100 dark:bg-neutral-800">
            <FileIcon className="h-5 w-5 text-neutral-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
              {attachment.filename}
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {attachment.contentType}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
          {attachment.projectName}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          {attachment.boardName}
        </span>
      </td>
      <td className="px-4 py-3">
        <button onClick={onTaskClick} className="text-left hover:underline">
          <p className="text-sm text-primary-600 dark:text-primary-400">{attachment.taskKey}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate max-w-[150px]">{attachment.taskTitle}</p>
        </button>
      </td>
      <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
        {formatFileSize(attachment.sizeBytes)}
      </td>
      <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
        {timeAgo}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={onView}
            className="p-1.5 rounded text-neutral-500 hover:bg-primary-100 hover:text-primary-600 dark:hover:bg-primary-900/30"
            title={t('files.viewFile')}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={onDownload}
            className="p-1.5 rounded text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-600"
            title={t('files.downloadFile')}
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded text-neutral-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
            title={t('files.deleteFile')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
